import React, { useCallback, useRef, useState } from "react";

import { Chip, FormControlLabel, Menu, MenuItem, Stack, Typography } from "@mui/material";
import { benchmarkColumns, benchmarkColumnsFreemium } from "constants/benchmark";
import { useAccountSelector, useReportsActions, useReportsQueriesSelector } from "store";

import { calcFieldsToDisplay, indexNames } from "../../../constants/reports";

interface Props {}

const parameterToKey: Record<string, string> = {
  IRR: "bm_irr",
  MOIC: "bm_moic",
  DPI: "bm_dpi",
  PME: "bm_pme"
};

export const ParametersPicker: React.FC<Props> = () => {
  const [pmeOpen, setPmeOpen] = useState(false);
  const pmeChipRef = useRef<HTMLDivElement>(null);
  const { setQueriesAction } = useReportsActions();
  const { benchmarkTableColumns, benchmarkIndexName } = useReportsQueriesSelector();
  const isFreemiumMode = useAccountSelector()?.is_freemium || false;

  const isParameterActive = useCallback(
    (parameter: string) => {
      const key = parameterToKey[parameter];
      return benchmarkTableColumns?.some((col) => col.aggregation.startsWith(key));
    },
    [benchmarkTableColumns]
  );

  const handleParameterClick = (parameter: string) => {
    if (parameter === "PME") {
      if (isFreemiumMode) return; // Disable PME in freemium mode
      setPmeOpen(true);
      return;
    }

    const key = parameterToKey[parameter];

    if (!key) return;

    if (isParameterActive(parameter)) {
      // Remove all columns with this key
      setQueriesAction({
        benchmarkTableColumns: benchmarkTableColumns?.filter((col) => !col.aggregation.startsWith(key)) || []
      });
    } else {
      // Add columns in specific order
      // Use freemium columns if in freemium mode, otherwise use regular columns
      const columnsSource = isFreemiumMode ? benchmarkColumnsFreemium : benchmarkColumns;
      const defaultColumns = columnsSource.filter((col) => col.aggregation === key);

      const currentColumns = benchmarkTableColumns || [];

      // Define order of parameters
      const order = ["bm_irr", "bm_dpi", "bm_moic", "bm_pme"];

      // Find position to insert new columns
      const position = order.indexOf(key);
      const nextKey = order[position + 1];

      // Find all existing columns of the same type
      const existingColumns = currentColumns.filter((col) => col.aggregation === key);

      if (existingColumns.length > 0) {
        // If we have columns of the same type, add new ones after them
        const lastIndex = currentColumns.indexOf(existingColumns[existingColumns.length - 1]);
        setQueriesAction({
          benchmarkTableColumns: [
            ...currentColumns.slice(0, lastIndex + 1),
            ...defaultColumns,
            ...currentColumns.slice(lastIndex + 1)
          ]
        });
      } else {
        // If no columns of the same type exist, insert before next parameter type
        const insertIndex = currentColumns.findIndex((col) => nextKey && col.aggregation.startsWith(nextKey));

        if (insertIndex === -1) {
          setQueriesAction({
            benchmarkTableColumns: [...currentColumns, ...defaultColumns]
          });
        } else {
          setQueriesAction({
            benchmarkTableColumns: [
              ...currentColumns.slice(0, insertIndex),
              ...defaultColumns,
              ...currentColumns.slice(insertIndex)
            ]
          });
        }
      }
    }
  };

  const handleSelectIndex = (indexName: string) => {
    setPmeOpen(false);
    // const pmeColumns = benchmarkTableColumns?.filter((col) => col.aggregation.startsWith("bm_pme")) || [];

    if (indexName === "none") {
      setQueriesAction({
        benchmarkTableColumns: benchmarkTableColumns?.filter((col) => !col.aggregation.startsWith("bm_pme")) || [],
        benchmarkIndexName: null
      });
    } else {
      const newPmeColumn = { aggregation: "bm_pme", key: "bm_pme" };
      setQueriesAction({
        benchmarkTableColumns: [
          ...(benchmarkTableColumns?.filter((col) => !col.aggregation.startsWith("bm_pme")) || []),
          newPmeColumn
        ],
        benchmarkIndexName: { index: indexName, name: indexNames[indexName] }
      });
    }
  };

  return (
    <Stack direction="row" gap="10px" alignItems="center">
      <Typography variant="body3" fontWeight="500">
        Parameters:
      </Typography>
      <Stack flexDirection="row" gap="10px" alignItems="center">
        {calcFieldsToDisplay.map((parameter) => {
          const isPME = parameter === "PME";
          const isDisabled = isPME && isFreemiumMode;

          return (
            <FormControlLabel
              key={parameter}
              label=""
              onClick={() => !isDisabled && handleParameterClick(parameter)}
              sx={{ margin: 0 }}
              control={
                <Chip
                  ref={isPME ? pmeChipRef : undefined}
                  label={parameter}
                  disabled={isDisabled}
                  sx={{
                    color: "#071F35",
                    fontSize: "12px",
                    height: "24px",
                    ...(isParameterActive(parameter) && {
                      backgroundColor: "#c3c9cf !important"
                    }),
                    ...(isDisabled && {
                      opacity: 0.5
                    })
                  }}
                />
              }
            />
          );
        })}
        <Menu anchorEl={pmeChipRef.current} open={pmeOpen} onClose={() => setPmeOpen(false)}>
          <MenuItem onClick={() => handleSelectIndex("none")}>None</MenuItem>
          {Object.entries(indexNames).map(([index, name]) => (
            <MenuItem
              key={index}
              selected={benchmarkTableColumns?.some(
                (col) => col.key === "bm_pme" && benchmarkIndexName?.index === index
              )}
              onClick={() => handleSelectIndex(index)}
            >
              {name}
            </MenuItem>
          ))}
        </Menu>
      </Stack>
    </Stack>
  );
};
